/**
 * 插画星球舞台的纯模型（WORLD-ART-117，WA）：谁上图、站在哪、昼还是夜。只读服务端给的结构化字段（presence、asleep、server_time、故事排序），
 * 不从 doing 文字里猜，不编位置、不编动作，不读看的人本机时钟。
 *
 * 底图与站位：r7k ASSET-WORLD-PLANET-BASE-01（I 10-16 审过，ADDENDUM-14B 第一节），standing-points.json 原字节在
 * public/ui-assets/WORLD-PLANET-BASE-01/v1/ 同目录；这里手写同一份（用例逐点核两者一致）。390×844 逻辑坐标，点是角色脚底中心；昼夜同一份。
 *
 * 挑人（I 10-16 认可的方案 2.1）：最多 11 位，9 个家门口、2 个路上（原定 12 位、3 个路上，P10 因点按区互压不用，见 STAGE_POINTS）；先今天有新鲜事的（故事排序里照片、相遇、事件、远行、TA 自己的小事），
 * 再醒着在家的、在外面的（在路上、到访中）、睡着的、在家说不清睡没睡的；同一档按 pet_id 哈希排，刷新不跳。说不清在哪的不上图。
 * 站哪：在家→家门口，在外面→路上，不站家门口；每位按 pet_id 有一间首选的屋，被占了按固定顺序往下找；路上满了就不上图。
 */
import { RhythmSkyValues, type PublicMapResident, type RhythmSky } from "@/shared/contracts";
import { actorScene } from "../actors/actorModel";
import type { DailyStories, DailyTier } from "./guestStoriesModel";

export type StagePlace = "home" | "path";
export type StagePoint = Readonly<{ id: string; x: number; y: number; place: StagePlace }>;

/** 底图逻辑尺寸（1170×2532 的三分之一） */
export const STAGE_W = 390;
export const STAGE_H = 844;
/** 站位单子说的角色高度（390 宽下） */
export const STAGE_ACTOR = 48;

/** standing-points.json（sha256 95f4c76b76ebf7bc2f23aba73b626f515c5aec53523024e7993c782f6fec821d）：home_door、home_porch 算家门口，path 算路上 */
export const PLANET_POINTS: readonly StagePoint[] = [
  { id: "P01", x: 194, y: 239, place: "home" },
  { id: "P02", x: 112, y: 281, place: "home" },
  { id: "P03", x: 323, y: 297, place: "home" },
  { id: "P04", x: 209, y: 344, place: "home" },
  { id: "P05", x: 51, y: 349, place: "home" },
  { id: "P06", x: 289, y: 414, place: "home" },
  { id: "P07", x: 53, y: 448, place: "home" },
  { id: "P08", x: 144, y: 527, place: "home" },
  { id: "P09", x: 283, y: 529, place: "home" },
  { id: "P10", x: 152, y: 262, place: "path" },
  { id: "P11", x: 119, y: 419, place: "path" },
  { id: "P12", x: 226, y: 470, place: "path" },
];

/**
 * 舞台实际用的站位：不用 P10。那条小路夹在 P01、P02 两户中间，三颗不小于 44px 的点按区在哪个尺寸都互相压——
 * 320 宽有效只剩七八成（设计评审 R1 高 4），390、375 宽 P10 那颗也凑不出一整块 44×44（R2 中）；角色最大 56px 而站位间距随画面放大，宽屏也一样压。
 * 所以上图最多 11 位：9 个家门口、2 个路上（I 10-16 Q4 原定 12 位，因点按区规矩少一个，交付时说明）。
 */
export const STAGE_POINTS: readonly StagePoint[] = PLANET_POINTS.filter((point) => point.id !== "P10");

/** 这一位在舞台上该站哪类地方：在家（睡着、醒着、说不清睡没睡）→家门口；在路上、到访中→路上；说不清在哪→不上图 */
export function stagePlace(resident: Pick<PublicMapResident, "presence" | "asleep">): StagePlace | null {
  const scene = actorScene(resident);
  if (scene === "home_asleep" || scene === "home_awake" || scene === "home_unknown") return "home";
  if (scene === "road" || scene === "place") return "path";
  return null;
}

/** 同一档里的先后（0 在前）：醒着在家 0、在外面 1、睡着 2、在家说不清睡没睡 3 */
function stateRank(resident: Pick<PublicMapResident, "presence" | "asleep">): number {
  const scene = actorScene(resident);
  if (scene === "home_awake") return 0;
  if (scene === "road" || scene === "place") return 1;
  if (scene === "home_asleep") return 2;
  return 3;
}

/** 按 pet_id 的稳定哈希（FNV-1a 加 murmur3 收尾混合，和 ResidentActor 的相位同一个写法，但取整数） */
export function stageHash(petId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < petId.length; i++) {
    h ^= petId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** 算「今天有新鲜事」的那几档：照片、相遇、事件、远行、TA 自己的小事。「此刻在外面」（out）只是状态，例行的（routine）不算 */
const FRESH: readonly DailyTier[] = ["photo", "met", "event", "trip", "post"];

/** 故事排序（6c2b 的 dailyStories）里有新鲜事的几位：pet_id → 名次（0 在前）；相遇那张里的几位同一个名次；一位在几张里取最前那张 */
export function stageStoryRank(daily: Pick<DailyStories, "stories">): Map<string, number> {
  const rank = new Map<string, number>();
  daily.stories.forEach((story, index) => {
    if (!FRESH.includes(story.tier)) return;
    for (const resident of story.residents) if (!rank.has(resident.pet_id)) rank.set(resident.pet_id, index);
  });
  return rank;
}

export type StageCast = Readonly<{ resident: PublicMapResident; point: StagePoint }>;

/**
 * 谁上图、站哪。storyRank：故事排序里有新鲜事的那几位（pet_id → 名次，0 在前），由页面按 dailyStories 算好给；没有就当都没有新鲜事。
 * 只算照片、相遇、事件、远行、TA 自己的小事（「此刻在外面」那档只是状态，不算新鲜事）。
 * 返回按脚底 y 从上到下排（画的时候下面的盖上面的；读屏、Tab 也照这个顺序，y 相同按 x）。
 */
export function stageCast(residents: readonly PublicMapResident[], storyRank: ReadonlyMap<string, number> = new Map(), points: readonly StagePoint[] = STAGE_POINTS): StageCast[] {
  const seen = new Set<string>();
  const candidates = residents.filter((resident) => {
    if (seen.has(resident.pet_id) || stagePlace(resident) === null) return false;
    seen.add(resident.pet_id);
    return true;
  });
  const story = (id: string) => storyRank.get(id) ?? Number.POSITIVE_INFINITY;
  candidates.sort((a, b) => {
    const sa = story(a.pet_id), sb = story(b.pet_id);
    if (sa !== sb) return sa < sb ? -1 : 1;
    const ra = stateRank(a), rb = stateRank(b);
    if (ra !== rb) return ra - rb;
    const ha = stageHash(a.pet_id), hb = stageHash(b.pet_id);
    if (ha !== hb) return ha < hb ? -1 : 1;
    return a.pet_id < b.pet_id ? -1 : a.pet_id > b.pet_id ? 1 : 0;
  });
  const taken = new Set<string>();
  const cast: StageCast[] = [];
  for (const resident of candidates) {
    const place = stagePlace(resident)!;
    const pool = points.filter((point) => point.place === place);
    if (!pool.length) continue;
    const first = stageHash(resident.pet_id) % pool.length;
    for (let step = 0; step < pool.length; step++) {
      const point = pool[(first + step) % pool.length];
      if (taken.has(point.id)) continue;
      taken.add(point.id);
      cast.push({ resident, point });
      break;
    }
  }
  return cast.sort((a, b) => a.point.y - b.point.y || a.point.x - b.point.x);
}

export type StageSky = "day" | "night";

/** 东八区的钟点（0～23）：只用服务器给的时刻（server_time），读不出为 null */
function chinaHour(serverTime: string | null | undefined): number | null {
  if (!serverTime) return null;
  const ms = Date.parse(serverTime);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms + 8 * 3_600_000).getUTCHours();
}

/**
 * 读 /public/world 的公开天色（B PUBLIC-SKY-117：星球整体一个值，和作息同一套太阳高度角算法，参考点东八区 120°E、30°N，每次返回现算）。
 * 契约生成物等 I 重生，这里先按附加字段读：只认 night、dawn、day、dusk 四个值，别的（缺了、null、认不出）都当没给。
 */
export function publicSky(value: unknown): RhythmSky | null {
  return typeof value === "string" && (RhythmSkyValues as readonly string[]).includes(value) ? (value as RhythmSky) : null;
}

/**
 * 昼还是夜（I 10-16 Q2 方案 A；B 的公开 sky 到了以它为准）：
 * 1. 给了公开天色：day、dawn→昼，night、dusk→夜；
 * 2. 在家、睡没睡说得清的居民里，睡着的多→夜，醒着的多→昼；
 * 3. 一样多或一位都说不清：用服务器时刻按东八区算，19:00–5:59 为夜；
 * 4. 都没有：null（页面不下判断）。
 */
export function stageSky({ sky = null, residents, serverTime = null }: { sky?: RhythmSky | null; residents: readonly Pick<PublicMapResident, "presence" | "asleep">[]; serverTime?: string | null }): StageSky | null {
  if (sky === "day" || sky === "dawn") return "day";
  if (sky === "night" || sky === "dusk") return "night";
  let asleep = 0;
  let awake = 0;
  for (const resident of residents) {
    if (resident.presence !== "at_home") continue;
    if (resident.asleep === true) asleep += 1;
    else if (resident.asleep === false) awake += 1;
  }
  if (asleep !== awake) return asleep > awake ? "night" : "day";
  const hour = chinaHour(serverTime);
  if (hour === null) return null;
  return hour >= 19 || hour < 6 ? "night" : "day";
}

/**
 * 画面怎么铺（点跟着底图走）：手机竖屏铺满（object-fit: cover 的缩放），左右多出来的居中裁，上下多出来的只从底下裁（底下是云，本来就在面板后面）——
 * 顶对齐，天上的月亮、太阳不往上挤到顶栏里（设计评审 R1 阻断 2：居中裁时 320×640 夜里月亮尖压到「加入星球」）；
 * 比 0.6 宽的屏（平板、桌面）按高度放下整张，左右居中，两边用同一张图放大模糊垫底。返回底图在容器里的缩放和左上角偏移（px）；量不到时为 null。
 */
export function stageFit(width: number, height: number): { scale: number; left: number; top: number; cover: boolean } | null {
  if (!(width > 0 && height > 0)) return null;
  const cover = width / height <= 0.6;
  const scale = cover ? Math.max(width / STAGE_W, height / STAGE_H) : height / STAGE_H;
  return { scale, left: (width - STAGE_W * scale) / 2, top: 0, cover };
}

/**
 * 卡开着时不重新排（换人、挪位会让被点的那一位从卡旁边跑开）：照旧站位，只换成这一刻的资料；
 * 不在名单里了、说不清在哪了、该站的地方类型变了（在家↔在外面）的不画。
 */
export function keepCast(prev: readonly StageCast[], residents: readonly PublicMapResident[]): StageCast[] {
  const byId = new Map(residents.map((resident) => [resident.pet_id, resident]));
  return prev.flatMap(({ resident, point }) => {
    const now = byId.get(resident.pet_id);
    return now && stagePlace(now) === point.place ? [{ resident: now, point }] : [];
  });
}

/** 站位一样（同几位、各站同一处）就算同一份：页面每次重画都会重算，一样的话沿用上一份，不让舞台跟着重排 */
export function castKey(cast: readonly StageCast[]): string {
  return cast.map(({ resident, point }) => `${resident.pet_id}@${point.id}`).join(",");
}
